import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/all";
import left1 from "../../assets/REMA/whatToExpect/left-1.png";
import left2 from "../../assets/REMA/whatToExpect/left-2.png";
import right1 from "../../assets/REMA/whatToExpect/right-1.png";
import right2 from "../../assets/REMA/whatToExpect/right-2.png";
import { useMediaQuery } from "react-responsive";
import { EVENT_EXPECTATION_DATA } from "./constants";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText);

// What to expect content data

// Reusable Decorative Image Component
const DecorativeImage = ({ src, alt }) => (
  <div>
    <div
      className="rema-image-container"
      style={{
        clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        transition: "none",
        overflow: "hidden",
      }}
    >
      <img
        src={src}
        alt={alt}
        className="mb-10 grayscale hover:grayscale-0 rema-image"
        style={{
          scale: "1",
          transformOrigin: "center",
          transition: "none",
        }}
      />
    </div>
    <div className="w-36 h-4 bg-[#A5D246] base-rect"></div>
  </div>
);

const WhatToExpect = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const textContentRef = useRef(null);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      // Split text animation for title (used on all devices)
      const titleSplit = new SplitText(titleRef.current, {
        type: "lines,words",
        linesClass: "overflow-hidden",
      });

      // Set initial states for images (desktop only)
      if (!isMobile) {
        gsap.set(sectionRef.current.querySelectorAll(".rema-image-container"), {
          clipPath: "polygon(0 0, 100% 0, 100% 0%, 0 0%)",
        });
        gsap.set(sectionRef.current.querySelectorAll(".rema-image"), {
          scale: 1.5,
        });
        gsap.set(sectionRef.current.querySelectorAll(".base-rect"), {
          width: 0,
        });
      }

      // Set initial states for text (all devices)
      gsap.set(titleSplit.words, { y: 100, opacity: 0 });
      gsap.set(textContentRef.current.querySelectorAll(".text-item"), {
        y: 50,
        opacity: 0,
      });
      gsap.set(textContentRef.current.querySelectorAll(".dividing-line"), {
        width: 0,
      });

      // Animate images & base-rects ONLY on desktop
      if (!isMobile) {
        tl.to(sectionRef.current.querySelectorAll(".rema-image-container"), {
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
          duration: 1,
          ease: "power1.out",
          stagger: 0.3,
        })
          .to(
            sectionRef.current.querySelectorAll(".rema-image"),
            {
              scale: 1,
              duration: 1,
              ease: "power2.out",
              stagger: 0.1,
            },
            "<"
          )
          .to(
            sectionRef.current.querySelectorAll(".base-rect"),
            {
              width: "160px",
              duration: 0.6,
              ease: "power2.out",
              stagger: 0.2,
            },
            "<"
          );
      }

      // Animate title (all devices)
      tl.to(
        titleSplit.words,
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          stagger: 0.15,
        },
        "<"
      )

        // Animate text items (all devices)
        .to(
          textContentRef.current.querySelectorAll(".text-item"),
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power2.out",
            stagger: 0.2,
          },
          "<"
        )
        // Animate dividing lines alongside text items
        .to(
          textContentRef.current.querySelectorAll(".dividing-line"),
          {
            width: "100%",
            duration: 0.6,
            ease: "power2.out",
            stagger: 0.2,
          },
          "<"
        );

      return () => {
        titleSplit.revert();
      };
    },
    { scope: sectionRef, dependencies: [isMobile] } // 👈 Important: Re-run if isMobile changes
  );

  return (
    <section
      ref={sectionRef}
      className="py-16 md:py-24 bg-[#CBF07F] text-white relative overflow-hidden min-h-screen flex items-center justify-center flex-col lg:flex-row"
    >
      <div className="container mx-auto px-4 md:px-10 flex justify-between gap-8">
        <div className="hidden lg:flex flex-col justify-between ">
          <DecorativeImage src={left1} alt="Decorative Left 1" />
          <DecorativeImage src={left2} alt="Decorative Left 2" />
        </div>
        <div className="w-full lg:max-w-md font-sora">
          <h2
            ref={titleRef}
            className="text-2xl md:text-[44px] leading-normal font-bold font-sora  text-[#11302D] uppercase text-balance"
          >
            What to Expect at REMA 2025
          </h2>
          <div ref={textContentRef} className="space-y-10 mt-16 lg:mt-[70px]">
            {EVENT_EXPECTATION_DATA.map((item, index, arr) => (
              <React.Fragment key={item.id}>
                <div
                  key={item.id}
                  className="text-[#11302D] flex gap-10 text-item"
                >
                  <h3 className="text-base md:text-lg font-semibold mb-3 ">
                    {`0${item.id}`}
                  </h3>
                  <div className=" opacity-80 hover:opacity-100 transition-opacity duration-300">
                    <h3 className="text-base md:text-lg font-semibold mb-3 ">
                      {item.title}
                    </h3>
                    <p className=" leading-relaxed text-sm md:text-base ">
                      {item.description}
                    </p>
                  </div>
                </div>
                {arr.length - 1 > index && (
                  <div className="h-[1px] w-full bg-[#11302D] dividing-line" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="hidden lg:flex flex-col justify-between ">
          <DecorativeImage src={right1} alt="Decorative Right 1" />
          <DecorativeImage src={right2} alt="Decorative Right 2" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:hidden gap-4 md:gap-8 w-full">
        <div className="flex flex-col gap-5">
          <div className="w-36 h-4 bg-[#A5D246] base-rect " />
          <img src={left1} alt="Decorative Left 1" />
        </div>
        <div className="flex flex-col gap-5">
          <div className="w-36 h-4 bg-[#A5D246] base-rect self-end" />
          <img src={left2} alt="Decorative Left 1" />
        </div>
        <div className="flex flex-col gap-5">
          <div className="w-36 h-4 bg-[#A5D246] base-rect self-end" />
          <img src={right1} alt="Decorative Left 1" />
        </div>
        <div className="flex flex-col gap-5">
          <div className="w-36 h-4 bg-[#A5D246] base-rect" />
          <img src={right2} alt="Decorative Left 1" />
        </div>
      </div>
    </section>
  );
};

export default WhatToExpect;
